import React, { useEffect, useState } from "react";
import apiClient from "../../api/axios";
import { toast } from "react-toastify";
import useFileUploader from "../../hooks/useFileUploader";
import { FaEdit, FaTrash, FaBoxes, FaListAlt, FaImage } from "react-icons/fa";
import usePath from "../../hooks/usePath";
import SafeImage from "../../components/common/SafeImage";

export default function ProductCategoryAdd() {
  // Category State
  const [categories, setCategories] = useState([]);
  const [catName, setCatName] = useState("");
  const [catNav, setCatNav] = useState("");
  const [catAgeValidation, setCatAgeValidation] = useState(false);
  const [catEditId, setCatEditId] = useState(null);
  const [catEditName, setCatEditName] = useState("");
  const [catEditNav, setCatEditNav] = useState("");
  const [catEditAgeValidation, setCatEditAgeValidation] = useState(false);
  // Subcategory State
  const [subcategories, setSubcategories] = useState([]);
  const [subCatName, setSubCatName] = useState("");
  const [subCatCategoryId, setSubCatCategoryId] = useState("");
  const [subCatEditId, setSubCatEditId] = useState(null);
  const [subCatEditName, setSubCatEditName] = useState("");
  const [subCatEditCategoryId, setSubCatEditCategoryId] = useState("");
  const [subCatImage, setSubCatImage] = useState(null);
  const [subCatEditImage, setSubCatEditImage] = useState(null);
  const [subCatImageUrl, setSubCatImageUrl] = useState("");
  const [subCatEditImageUrl, setSubCatEditImageUrl] = useState("");
  const [subCatEditIsActive, setSubCatEditIsActive] = useState(true);

  const { uploadImage, uploadStatus } = useFileUploader();
  const [catImage, setCatImage] = useState(null);
  const [catEditImage, setCatEditImage] = useState(null);
  const [catImageUrl, setCatImageUrl] = useState("");
  const [catEditImageUrl, setCatEditImageUrl] = useState("");
  const [catEditIsActive, setCatEditIsActive] = useState(true);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [catLoading, setCatLoading] = useState(false);
  const [subCatLoading, setSubCatLoading] = useState(false);
  const [catEditLoadingId, setCatEditLoadingId] = useState(null);
  const [catDeleteLoadingId, setCatDeleteLoadingId] = useState(null);
  const [subCatEditLoadingId, setSubCatEditLoadingId] = useState(null);
  const [subCatDeleteLoadingId, setSubCatDeleteLoadingId] = useState(null);

  //category , sub category table loading
  const [catTableLoading, setCatTableLoading] = useState(true);
  const [subCatTableLoading, setSubCatTableLoading] = useState(true);

  // Fetch categories and subcategories
  const fetchCategories = async () => {
    try {
      setCatTableLoading(true);
      const res = await apiClient.get("public/categories");
      setCategories(res.data.data || []);
    } catch (e) {
      toast.error("Failed to fetch categories");
    } finally {
      setCatTableLoading(false);
    }
  };
  const fetchSubcategories = async (categoryId = null) => {
    try {
      setSubCatTableLoading(true);
      let url = "public/subcategories";
      if (categoryId) {
        url += `?category_id=${categoryId}`;
      }
      const res = await apiClient.get(url);
      setSubcategories(res.data.data || []);
    } catch (e) {
      toast.error("Failed to fetch subcategories");
    } finally {
      setSubCatTableLoading(false);
    }
  };
  // not fill category form clear button not show
  const isCategoryFormDirty =
    catName.trim() !== "" ||
    catNav.trim() !== "" ||
    catAgeValidation !== false ||
    catImage !== null ||
    catImageUrl !== "";

  //not fill sub category form clear button not show
  const isSubCategoryFormDirty =
    subCatName.trim() !== "" ||
    // (subCatCategoryId && subCatCategoryId !== '') ||
    subCatImage !== null ||
    subCatImageUrl !== "";

  // useEffect(() => {
  //   fetchCategories();
  //   // We'll fetch subcategories in the other useEffect when selectedCategoryId changes
  //   // or fetch all subcategories if no category is selected
  //   if (!selectedCategoryId) {
  //     fetchSubcategories();
  //   }
  // }, [selectedCategoryId]); // Include selectedCategoryId as a dependency

  useEffect(() => {
    fetchCategories(); // ✅ Only once
    fetchSubcategories(); // Optional initial fetch
  }, []); // Empty dependency

  const CategoryTable = React.memo(
    ({ categories, selectedCategoryId, onSelect }) => {
      return (
        <table className="table">
          <tbody>
            {categories.map((cat) => (
              <tr
                key={cat.category_id}
                className={
                  selectedCategoryId == cat.category_id ? "table-active" : ""
                }
                onClick={() => onSelect(cat.category_id)}
              >
                <td>{cat.category_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
  );

  // When a category is selected, set subCatCategoryId for the add subcategory form
  useEffect(() => {
    if (selectedCategoryId && !subCatEditId) {
      setSubCatCategoryId(selectedCategoryId);
    }
  }, [selectedCategoryId, subCatEditId]);

  // Fetch subcategories when a category is selected
  useEffect(() => {
    if (selectedCategoryId) {
      // Fetch subcategories filtered by category_id from the API
      fetchSubcategories(selectedCategoryId);
    } else {
      // If no category is selected, fetch all subcategories
      fetchSubcategories();
    }
  }, [selectedCategoryId]);

  //default category select
  useEffect(() => {
    if (categories.length > 0 && !selectedCategoryId) {
      setSelectedCategoryId(categories[0].category_id);
    }
  }, [categories, selectedCategoryId]);

  //when select category automatically clear category  and subcategory form fields
  useEffect(() => {
    // When a new category is selected, clear all form fields
    // 🔹 Reset category edit form
    setCatEditId(null);
    setCatEditName("");
    setCatEditNav("");
    setCatEditAgeValidation(false);
    setCatEditImage(null);
    setCatEditImageUrl("");
    setCatEditIsActive(true);

    // 🔹 Reset category add form
    setCatName("");
    setCatNav("");
    setCatAgeValidation(false);
    setCatImage(null);
    setCatImageUrl("");

    // 🔹 Reset subcategory add form
    setSubCatName("");
    setSubCatCategoryId(selectedCategoryId || "");
    setSubCatImage(null);
    setSubCatImageUrl("");

    // 🔹 Reset subcategory edit form (if you have edit mode for subcategories)
    setSubCatEditId(null);
    setSubCatEditName("");
    setSubCatEditImage(null);
    setSubCatEditImageUrl("");
  }, [selectedCategoryId]);

  // Category CRUD
  const handleAddCategory = async (e) => {
    e.preventDefault();
    setCatLoading(true);

    if (!catName.trim() || !catNav.trim()) {
      toast.error("All fields required");
      setCatLoading(false);
      return;
    }

    // Check for duplicate category name
    const isDuplicate = categories.some(
      (cat) => cat.category_name.trim().toLowerCase() === catName.trim().toLowerCase()
    );

    if (isDuplicate) {
      toast.error("Category name already taken");
      setCatLoading(false);
      return;
    }
    if (!catImage) {
      toast.error("Please select an image");
      setCatLoading(false);
      return;
    }

    // Upload image
    let imageUrl = await uploadImage(catImage, "categories", catName.replace(/\s+/g, "_"));
    if (!imageUrl) {
      toast.error("Image upload failed");
      setCatLoading(false);
      return;
    }

    try {
      // Add category
      const res = await apiClient.post("/categories", {
        category_name: catName,
        category_nav: catNav,
        category_image: imageUrl,
        age_validation: catAgeValidation,
      });

      // Reset input fields
      setCatName("");
      setCatNav("");
      setCatAgeValidation(false);
      setCatImage(null);
      setCatImageUrl("");

      // ✅ Fetch updated categories
      const updated = await apiClient.get("public/categories");
      setCategories(updated.data.data || []);
      toast.success("Category added");

      // ✅ Automatically select the newly added category
      const newCatId = res.data?.data?.category_id || null; // depends on your backend response
      if (newCatId) {
        setSelectedCategoryId(newCatId);
        fetchSubcategories(newCatId);
      } else if (updated.data.data?.length > 0) {
        // fallback if API doesn’t return the new category ID
        const lastAdded = updated.data.data[updated.data.data.length - 1];
        setSelectedCategoryId(lastAdded.category_id);
        fetchSubcategories(lastAdded.category_id);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to add category");
    } finally {
      setCatLoading(false);
    }
  };

  const handleEditCategory = (cat) => {
    setCatEditId(cat.category_id);
    setCatEditName(cat.category_name);
    setCatEditNav(cat.category_nav || "");
    setCatEditAgeValidation(cat.age_validation || false);
    setCatEditImageUrl(cat.category_image || "");
    setCatEditIsActive(cat.is_active !== false);
  };
  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    setCatLoading(true);
    setCatEditLoadingId(catEditId);
    if (!catEditName.trim() || !catEditNav.trim()) {
      toast.error("All fields required");
      setCatLoading(false);
      setCatEditLoadingId(null);
      return;
    }

    // Check for duplicate category name excluding current one
    const isDuplicate = categories.some(
      (cat) =>
        cat.category_name.trim().toLowerCase() === catEditName.trim().toLowerCase() &&
        cat.category_id !== catEditId
    );

    if (isDuplicate) {
      toast.error("Category name already taken");
      setCatLoading(false);
      setCatEditLoadingId(null);
      return;
    }

    let imageUrl = catEditImageUrl;

    if (catEditImage) {
      imageUrl = await uploadImage(
        catEditImage,
        "categories",
        catEditName.replace(/\s+/g, "_")
      );
      if (!imageUrl) {
        setCatLoading(false);
        setCatEditLoadingId(null);
        return;
      } // upload failed
    }
    try {
      await apiClient.put(`/categories/${catEditId}`, {
        category_name: catEditName,
        category_nav: catEditNav,
        category_image: imageUrl,
        age_validation: catEditAgeValidation,
        is_active: catEditIsActive,
      });
      toast.success("Category updated");
      setCatEditId(null);
      setCatEditName("");
      setCatEditNav("");
      setCatEditAgeValidation(false);
      setCatEditImage(null);
      setCatEditImageUrl("");
      setCatEditIsActive(true);
      fetchCategories();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to update category");
    } finally {
      setCatLoading(false);
      setCatEditLoadingId(null);
    }
  };

  const handleDeleteCategory = async (id) => {
    // if (!window.confirm('Delete this category?')) return;
    setCatLoading(true);
    setCatDeleteLoadingId(id);
    try {
      await apiClient.delete(`/categories/${id}`);

      // Fetch updated categories
      const res = await apiClient.get("public/categories");
      setCategories(res.data.data || []);
      toast.success("Category deleted");

      // Reset selected category if it was deleted
      if (selectedCategoryId == id) {
        setSelectedCategoryId(res.data.data?.[0]?.category_id || null);
      }

      // Fetch subcategories for the new selected category (if any)
      if (res.data.data?.length > 0) {
        fetchSubcategories(res.data.data[0].category_id);
      } else {
        setSubcategories([]);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to delete category");
    } finally {
      setCatLoading(false);
      setCatDeleteLoadingId(null);
    }
  };

  const confirmDeleteCategory = (id) => {
    const ToastContent = ({ closeToast }) => (
      <div className="d-flex flex-column">
        <span>
          Are you sure you want to delete this category? sub categories also
          delete!
        </span>
        <div className="mt-2 d-flex justify-content-end gap-2">
          <button
            className="btn btn-sm btn-danger"
            onClick={async () => {
              closeToast();
              await handleDeleteCategory(id);
            }}
          >
            Yes
          </button>
          <button className="btn btn-sm btn-secondary" onClick={closeToast}>
            No
          </button>
        </div>
      </div>
    );

    toast.info(<ToastContent />, {
      autoClose: false, // stay until user clicks
      closeOnClick: false,
      draggable: false,
    });
  };

  //cancel button function
  const handleCancelCategory = () => {
    setCatName("");
    setCatNav("");
    setCatAgeValidation(false);
    setCatImage(null);
    setCatImageUrl("");
  };

  // Subcategory CRUD
  const handleAddSubcategory = async (e) => {
    e.preventDefault();
    setSubCatLoading(true);
    if (!subCatName.trim() || !subCatCategoryId) {
      toast.error("All fields required");
      setSubCatLoading(false);
      return;
    }

    // Check for duplicate subcategory name within the same category
    const isDuplicate = subcategories.some(
      (sub) =>
        sub.subcategory_name.trim().toLowerCase() === subCatName.trim().toLowerCase() &&
        sub.category_id == subCatCategoryId
    );

    if (isDuplicate) {
      toast.error("Subcategory name already taken");
      setSubCatLoading(false);
      return;
    }
    if (!subCatImage) {
      toast.error("Please select a subcategory image");
      setSubCatLoading(false);
      return;
    }

    let imageUrl = await uploadImage(
      subCatImage,
      "subcategories",
      subCatName.replace(/\s+/g, "_")
    );
    if (!imageUrl) {
      toast.error("Subcategory image upload failed");
      setSubCatLoading(false);
      return;
    }

    try {
      await apiClient.post("/subcategories", {
        subcategory_name: subCatName,
        category_id: subCatCategoryId,
        subcategory_image: imageUrl,
        is_active: subCatEditIsActive,
      });

      toast.success("Subcategory added");
      setSubCatName("");
      setSubCatImage(null);
      setSubCatImageUrl("");
      setSubCatEditIsActive(true);
      // <-- Fetch only the active category's subcategories
      fetchSubcategories(selectedCategoryId);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to add subcategory");
    } finally {
      setSubCatLoading(false);
    }
  };

  // edit button function sub category
  const handleEditSubcategory = (sub) => {
    setSubCatEditId(sub.subcategory_id);
    setSubCatEditName(sub.subcategory_name);
    setSubCatEditCategoryId(sub.category_id);
    setSubCatEditImageUrl(sub.subcategory_image || "");
    setSubCatEditIsActive(sub.is_active !== false);
  };

  // update subcategory
  const handleUpdateSubcategory = async (e) => {
    e.preventDefault();
    setSubCatLoading(true);
    setSubCatEditLoadingId(subCatEditId);
    if (!subCatEditName.trim() || !subCatEditCategoryId) {
      toast.error("All fields required");
      setSubCatLoading(false);
      setSubCatEditLoadingId(null);
      return;
    }

    // Check for duplicate subcategory name excluding current one
    const isDuplicate = subcategories.some(
      (sub) =>
        sub.subcategory_name.trim().toLowerCase() === subCatEditName.trim().toLowerCase() &&
        sub.category_id == subCatEditCategoryId &&
        sub.subcategory_id !== subCatEditId
    );

    if (isDuplicate) {
      toast.error("Subcategory name already taken");
      setSubCatLoading(false);
      setSubCatEditLoadingId(null);
      return;
    }

    let imageUrl = subCatEditImageUrl;
    if (subCatEditImage) {
      imageUrl = await uploadImage(
        subCatEditImage,
        "subcategories",
        subCatEditName.replace(/\s+/g, "_")
      );
      if (!imageUrl) {
        toast.error("Subcategory image upload failed");
        setSubCatLoading(false);
        setSubCatEditLoadingId(null);
        return;
      }
    }

    try {
      await apiClient.put(`/subcategories/${subCatEditId}`, {
        subcategory_name: subCatEditName,
        category_id: subCatEditCategoryId,
        subcategory_image: imageUrl,
        is_active: subCatEditIsActive,
      });
      toast.success("Subcategory updated");

      // Reset edit form
      setSubCatEditId(null);
      setSubCatEditName("");
      setSubCatEditCategoryId("");
      setSubCatEditImage(null);
      setSubCatEditImageUrl("");
      setSubCatEditIsActive(true);
      // Fetch only the active/selected category's subcategories
      fetchSubcategories(selectedCategoryId);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to update subcategory");
    } finally {
      setSubCatLoading(false);
      setSubCatEditLoadingId(null);
    }
  };

  // Delete subcategory
  const handleDeleteSubcategory = async (id) => {
    // if (!window.confirm('Delete this subcategory?')) return;
    setSubCatLoading(true);
    setSubCatDeleteLoadingId(id);
    try {
      await apiClient.delete(`/subcategories/${id}`);
      toast.success("Subcategory deleted");
      fetchSubcategories(selectedCategoryId);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to delete subcategory");
    } finally {
      setSubCatLoading(false);
      setSubCatDeleteLoadingId(null);
    }
  };

  const confirmDeleteSubcategory = (id) => {
    const ToastContent = ({ closeToast }) => (
      <div>
        <span>Are you sure you want to delete this subcategory?</span>
        <div className="mt-2 d-flex justify-content-end gap-2">
          <button
            className="btn btn-sm btn-danger"
            onClick={async () => {
              closeToast();
              await handleDeleteSubcategory(id); // call actual delete
            }}
          >
            Yes
          </button>
          <button className="btn btn-sm btn-secondary" onClick={closeToast}>
            No
          </button>
        </div>
      </div>
    );

    toast.info(<ToastContent />, {
      autoClose: false, // prevent auto close
      closeOnClick: false, // prevent closing on click
      draggable: false, // optional
    });
  };

  //subcategory cancel button function
  const handleCancelSubcategory = () => {
    setSubCatName("");
    setSubCatCategoryId("");
    setSubCatImage(null);
    setSubCatImageUrl("");
  };

  // Since we're now fetching filtered subcategories directly from the API,
  // we can just use the subcategories state directly
  const filteredSubcategories = subcategories;

  // Log subcategories whenever they change
  useEffect(() => { }, [selectedCategoryId, subcategories]);

  // Handle image uploads for category
  const handleCatImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCatImage(file);
      setCatImageUrl(URL.createObjectURL(file));
    }
  };

  // Handle image uploads for subcategory
  const handleSubCatImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSubCatImage(file);
      setSubCatImageUrl(URL.createObjectURL(file));
    }
  };

  // Handle image uploads for category edit
  const handleCatEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCatEditImage(file);
      setCatEditImageUrl(URL.createObjectURL(file));
    }
  };

  // Handle image uploads for subcategory edit
  const handleSubCatEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSubCatEditImage(file);
      setSubCatEditImageUrl(URL.createObjectURL(file));
    }
  };

  const catEditImagePreview = catEditImage
    ? URL.createObjectURL(catEditImage)
    : null;
  const subCatEditImagePreview = subCatEditImage
    ? URL.createObjectURL(subCatEditImage)
    : null;

  return (
    <div className="container mt-4">
      <div className="bg-white rounded shadow-sm p-4 mb-4 border">
        <h2
          className="fw-bold mb-0"
          style={{ fontSize: "2rem", letterSpacing: "0.5px" }}
        >
          Product Category Management
        </h2>
      </div>
      <div className="row">
        <div className="col-md-6">
          <div className="d-flex align-items-center mb-3">
            <FaBoxes className="me-2 text-primary" size={24} />
            <h4 className="mb-0 fw-semibold" style={{ letterSpacing: "0.2px" }}>
              Categories
            </h4>
          </div>
          {/* Category Add Form (modern design) */}
          {!catEditId && (
            <div className="card mb-4 shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title mb-3 fw-semibold">Add Category</h5>
                <form onSubmit={handleAddCategory}>
                  <div className="row g-3 align-items-center">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Category Name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Category name"
                        value={catName}
                        onChange={(e) => setCatName(e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Category Nav
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Category nav"
                        value={catNav}
                        onChange={(e) => setCatNav(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="row mt-3">
                    <div className="col-md-12">
                      <div className="d-flex align-items-center justify-content-between p-3 border rounded-2 bg-white">
                        <label
                          htmlFor="catAgeValidationToggle"
                          className="mb-0 fw-semibold text-muted"
                        >
                          Age verification required (18+)
                        </label>
                        <div className="form-check form-switch m-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="catAgeValidationToggle"
                            checked={catAgeValidation}
                            onChange={(e) =>
                              setCatAgeValidation(e.target.checked)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row g-3 align-items-center mt-2">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Category Image
                      </label>
                      <div className="d-flex align-items-center gap-3">
                        <input
                          type="file"
                          id="catImageInput"
                          style={{ display: "none" }}
                          accept="image/*"
                          onChange={handleCatImageChange}
                        />
                        <label
                          htmlFor="catImageInput"
                          className="btn btn-outline-secondary d-flex align-items-center gap-2 mb-0"
                        >
                          <FaImage />{" "}
                          {catImageUrl ? "Change Image" : "Choose Image"}
                        </label>
                        {catImageUrl && (
                          <SafeImage
                            src={catImageUrl}
                            alt="Preview"
                            style={{
                              width: 48,
                              height: 48,
                              objectFit: "cover",
                              borderRadius: 6,
                              border: "1px solid #dee2e6",
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="row g-3 align-items-center mt-2">
                    <div className="col-md-12 d-flex align-items-end justify-content-end">
                      {isCategoryFormDirty && (
                        <button
                          type="button"
                          className="btn btn-secondary px-4 mx-4"
                          onClick={handleCancelCategory}
                          disabled={catLoading}
                        >
                          Clear
                        </button>
                      )}
                      <button
                        className="btn btn-primary px-4"
                        type="submit"
                        disabled={catLoading}
                      >
                        {catLoading ? (
                          <span className="spinner-border spinner-border-sm me-2" />
                        ) : null}
                        Save
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Category Edit Form (modern design) */}

          {catEditId && (
            <div className="card mb-4 shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title mb-3 fw-semibold">Edit Category</h5>

                <form onSubmit={handleUpdateCategory}>
                  {/* Row 1 - Category Name & Nav */}
                  <div className="row g-3 align-items-center">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Category Name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter category name"
                        value={catEditName}
                        onChange={(e) => setCatEditName(e.target.value)}
                        required
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Category Nav
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter category nav"
                        value={catEditNav}
                        onChange={(e) => setCatEditNav(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  {/* Row 2 - Age Verification */}
                  <div className="row mt-3">
                    <div className="col-12">
                      <div className="d-flex align-items-center justify-content-between p-3 border rounded-2 bg-white flex-wrap">
                        <label
                          htmlFor="catEditAgeValidationToggle"
                          className="mb-0 fw-semibold text-muted"
                        >
                          Age verification required (18+)
                        </label>
                        <div className="form-check form-switch m-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="catEditAgeValidationToggle"
                            checked={catEditAgeValidation}
                            onChange={(e) =>
                              setCatEditAgeValidation(e.target.checked)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Row 3 - Image and Status */}
                  <div className="row g-4 mt-3">
                    {/* Category Image Section */}
                    <div className="col-md-6 d-flex flex-column align-items-center text-center">
                      <label className="form-label fw-semibold mb-2">
                        Category Image
                      </label>

                      {/* Image Preview */}
                      {(catEditImagePreview || catEditImageUrl) && (
                        <SafeImage
                          src={catEditImagePreview || usePath(catEditImageUrl)}
                          alt="Preview"
                          className="img-fluid rounded-3 border mb-3"
                          style={{
                            width: "160px",
                            height: "160px",
                            objectFit: "cover",
                          }}
                        />
                      )}

                      {/* Upload Button */}
                      <div>
                        <input
                          type="file"
                          id="catEditImageInput"
                          style={{ display: "none" }}
                          accept="image/*"
                          onChange={handleCatEditImageChange}
                        />
                        <label
                          htmlFor="catEditImageInput"
                          className="btn btn-outline-secondary d-flex align-items-center gap-2 mb-0"
                        >
                          <FaImage />
                          {catEditImagePreview || catEditImageUrl
                            ? "Change Image"
                            : "Choose Image"}
                        </label>
                      </div>
                    </div>

                    {/* Active Status + Buttons */}
                    <div className="col-md-6 d-flex flex-column justify-content-between">
                      <div className="form-check form-switch align-self-end mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          checked={!!catEditIsActive}
                          onChange={(e) => setCatEditIsActive(e.target.checked)}
                          id="catIsActiveSwitch"
                          style={{ width: 48, height: 24, cursor: "pointer" }}
                        />
                        <label
                          className="form-check-label ms-2 fw-semibold"
                          htmlFor="catIsActiveSwitch"
                        >
                          {catEditIsActive ? (
                            <span className="text-success">Active</span>
                          ) : (
                            <span className="text-danger">Inactive</span>
                          )}
                        </label>
                      </div>

                      {/* Buttons */}
                      <div className="d-flex gap-2 justify-content-end mt-auto flex-wrap">
                        <button
                          className="btn btn-primary px-4"
                          type="submit"
                          disabled={catLoading}
                        >
                          {catLoading && (
                            <span className="spinner-border spinner-border-sm me-2" />
                          )}
                          Update
                        </button>
                        <button
                          className="btn btn-secondary px-4"
                          type="button"
                          onClick={() => {
                            setCatEditId(null);
                            setCatEditName("");
                            setCatEditNav("");
                            setCatEditImage(null);
                            setCatEditImageUrl("");
                          }}
                          disabled={catLoading}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Professional Category Table */}
          <div
            className="table-responsive mb-4"
            style={{
              background: "#fff",
              maxHeight: "400px",   // 👈 height limit
              overflowY: "auto"     // 👈 vertical scroll
            }}
          >

            <table className="table table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 60 }}>Image</th>
                  <th>Name</th>
                  <th style={{ width: 140 }}>Age Restriction</th>
                  <th style={{ width: 120 }}>Status</th>
                  <th style={{ width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {catTableLoading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : categories.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      No categories found.
                    </td>
                  </tr>
                ) : (
                  categories.map((cat) => (
                    <tr
                      key={cat.category_id}
                      className={
                        selectedCategoryId === cat.category_id
                          ? "table-active"
                          : ""
                      }
                      style={{
                        cursor: "pointer",
                        backgroundColor:
                          selectedCategoryId === cat.category_id
                            ? "#d7e7f1ff"
                            : "inherit", // light blue
                      }}
                      onClick={() => setSelectedCategoryId(cat.category_id)}
                    >
                      <td>
                        {cat.category_image ? (
                          <SafeImage
                            src={usePath(cat.category_image)}
                            alt={cat.category_name}
                            className="rounded border"
                            style={{
                              width: 44,
                              height: 44,
                              objectFit: "cover",
                              background: "#f8f9fa",
                            }}
                          />
                        ) : (
                          <span className="text-muted">No image</span>
                        )}
                      </td>
                      <td
                        style={{
                          maxWidth: 180,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={cat.category_name}
                      >
                        <strong>{cat.category_name}</strong>
                      </td>
                      <td>
                        {cat.age_validation ? (
                          <span className="badge bg-warning text-dark">
                            <i className="ri-shield-check-line me-1"></i>
                            18+ Required
                          </span>
                        ) : (
                          <span className="badge bg-success">
                            <i className="ri-check-line me-1"></i>
                            All Ages
                          </span>
                        )}
                      </td>
                      <td>
                        <span
                          className={`badge ${cat.is_active ? "bg-success" : "bg-danger"
                            }`}
                          title={cat.is_active ? "Active" : "Inactive"}
                        >
                          {cat.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex justify-content-end align-items-center gap-2">
                          <button
                            className="btn btn-outline-primary btn-sm"
                            title="Edit"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditCategory(cat);
                            }}
                            disabled={catEditLoadingId == cat.category_id}
                          >
                            {catEditLoadingId == cat.category_id ? (
                              <span className="spinner-border spinner-border-sm" />
                            ) : (
                              <FaEdit />
                            )}
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm"
                            title="Delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmDeleteCategory(cat.category_id);
                            }}
                            disabled={catDeleteLoadingId == cat.category_id}
                          >
                            {catDeleteLoadingId == cat.category_id ? (
                              <span className="spinner-border spinner-border-sm" />
                            ) : (
                              <FaTrash />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Subcategories */}
        <div className="col-md-6">
          <div className="d-flex align-items-center mb-3">
            <FaListAlt className="me-2 text-success" size={24} />
            <h4 className="mb-0 fw-semibold" style={{ letterSpacing: "0.2px" }}>
              Subcategories
            </h4>
          </div>
          {!selectedCategoryId && (
            <div className="alert alert-info">
              Select a category to view its subcategories.
            </div>
          )}
          {/* Subcategory Add Form (modern design) */}
          {!subCatEditId && (
            <div className="card mb-4 shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title mb-3 fw-semibold">Add Subcategory</h5>
                <form onSubmit={handleAddSubcategory}>
                  <div className="row g-3 align-items-center">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Subcategory Name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Subcategory name"
                        value={subCatName}
                        onChange={(e) => setSubCatName(e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Category</label>
                      {/* <select
                          className="form-select"
                                  value={selectedCategoryId || subCatCategoryId}
                                  onChange={e => setSubCatCategoryId(e.target.value)}
                                  disabled={!!selectedCategoryId}
                        >
                          <option value="">Select Category</option>
                          {categories.map(cat => (
                            <option key={cat.category_id} value={cat.category_id}>{cat.category_name}</option>
                          ))}
                        </select> */}
                      <div className="form-control">
                        {categories.find(
                          (cat) =>
                            cat.category_id ==
                            (selectedCategoryId || subCatCategoryId)
                        )?.category_name || "No Category Selected"}
                      </div>
                    </div>
                  </div>
                  <div className="row g-3 align-items-center mt-2">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Subcategory Image
                      </label>
                      <div className="d-flex align-items-center gap-3">
                        <input
                          type="file"
                          id="subCatImageInput"
                          style={{ display: "none" }}
                          accept="image/*"
                          onChange={handleSubCatImageChange}
                        />
                        <label
                          htmlFor="subCatImageInput"
                          className="btn btn-outline-secondary d-flex align-items-center gap-2 mb-0"
                        >
                          <FaImage />{" "}
                          {subCatImageUrl ? "Change Image" : "Choose Image"}
                        </label>
                        {subCatImageUrl && (
                          <SafeImage
                            src={subCatImageUrl}
                            alt="Preview"
                            style={{
                              width: 48,
                              height: 48,
                              objectFit: "cover",
                              borderRadius: 6,
                              border: "1px solid #dee2e6",
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="row g-3 align-items-center mt-2">
                    <div className="col-md-12 d-flex align-items-end justify-content-end">
                      {isSubCategoryFormDirty && (
                        <button
                          type="button"
                          className="btn btn-secondary px-4 mx-4"
                          onClick={handleCancelSubcategory}
                          disabled={subCatLoading}
                        >
                          Clear
                        </button>
                      )}
                      <button
                        className="btn btn-primary px-4"
                        type="submit"
                        disabled={subCatLoading}
                      >
                        {subCatLoading ? (
                          <span className="spinner-border spinner-border-sm me-2" />
                        ) : null}
                        Save
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}
          {/* Subcategory Edit Form (modern design) */}
          {subCatEditId && (
            <div className="card mb-4 shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title mb-3 fw-semibold">
                  Edit Subcategory
                </h5>
                <form onSubmit={handleUpdateSubcategory}>
                  <div className="row g-3 align-items-center">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Subcategory Name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Subcategory name"
                        value={subCatEditName}
                        onChange={(e) => setSubCatEditName(e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Category</label>
                      <select
                        className="form-select"
                        value={subCatEditCategoryId}
                        onChange={(e) =>
                          setSubCatEditCategoryId(e.target.value)
                        }
                      >
                        <option value="">Select Category</option>
                        {categories.map((cat) => (
                          <option key={cat.category_id} value={cat.category_id}>
                            {cat.category_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="row g-3 align-items-center mt-2">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Subcategory Image
                      </label>
                      <div className="d-flex align-items-center gap-3">
                        <input
                          type="file"
                          id="subCatEditImageInput"
                          style={{ display: "none" }}
                          accept="image/*"
                          onChange={handleSubCatEditImageChange}
                        />
                        <label
                          htmlFor="subCatEditImageInput"
                          className="btn btn-outline-secondary d-flex align-items-center gap-2 mb-0"
                        >
                          <FaImage />{" "}
                          {subCatEditImagePreview || subCatEditImageUrl
                            ? "Change Image"
                            : "Choose Image"}
                        </label>
                        {(subCatEditImagePreview || subCatEditImageUrl) && (
                          <SafeImage
                            src={subCatEditImagePreview || usePath(subCatEditImageUrl)}
                            alt="Preview"
                            style={{
                              width: 48,
                              height: 48,
                              objectFit: "cover",
                              borderRadius: 6,
                              border: "1px solid #dee2e6",
                            }}
                          />
                        )}
                      </div>
                    </div>
                    <div className="col-md-6 d-flex align-items-end justify-content-end gap-2">
                      <div className="w-100">
                        <div className="form-check form-switch mb-3">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            checked={!!subCatEditIsActive}
                            onChange={(e) =>
                              setSubCatEditIsActive(e.target.checked)
                            }
                            id="subCatIsActiveSwitch"
                            style={{ width: 48, height: 24, cursor: "pointer" }}
                          />
                          <label
                            className="form-check-label ms-2 fw-semibold"
                            htmlFor="subCatIsActiveSwitch"
                            style={{ userSelect: "none" }}
                          >
                            {subCatEditIsActive ? (
                              <span className="text-success">Active</span>
                            ) : (
                              <span className="text-danger">Inactive</span>
                            )}
                          </label>
                        </div>
                        <div className="d-flex gap-2 w-100 justify-content-end">
                          <button
                            className="btn btn-primary px-4"
                            type="submit"
                            disabled={subCatLoading}
                          >
                            {subCatLoading ? (
                              <span className="spinner-border spinner-border-sm me-2" />
                            ) : null}
                            Update
                          </button>
                          <button
                            className="btn btn-secondary px-4"
                            type="button"
                            onClick={() => {
                              setSubCatEditId(null);
                              setSubCatEditName("");
                              setSubCatEditCategoryId("");
                            }}
                            disabled={subCatLoading}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}
          {/* Professional Subcategory Table */}
          <div
            className="table-responsive mb-4"
            style={{
              background: "#fff",
              maxHeight: "400px",   // 👈 height limit
              overflowY: "auto"     // 👈 vertical scroll
            }}
          >
            <table className="table table-hover align-middle">

              <thead className="table-light">
                <tr>
                  <th style={{ width: 40 }}>Image</th>
                  <th>Name</th>
                  <th>Category</th>
                  <th style={{ width: 80 }}>Status</th>
                  <th style={{ width: 100 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {subCatTableLoading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-4">
                      <div
                        className="spinner-border text-success"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredSubcategories.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      No subcategories found.
                    </td>
                  </tr>
                ) : (
                  filteredSubcategories.map((sub) => (
                    <tr key={sub.subcategory_id}>
                      <td>
                        {sub.subcategory_image ? (
                          <SafeImage
                            src={usePath(sub.subcategory_image)}
                            alt={sub.subcategory_name}
                            className="rounded border"
                            style={{
                              width: 32,
                              height: 32,
                              objectFit: "cover",
                              background: "#f8f9fa",
                            }}
                          />
                        ) : (
                          <span className="text-muted">No image</span>
                        )}
                      </td>
                      <td
                        style={{
                          maxWidth: 160,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={sub.subcategory_name}
                      >
                        <strong>{sub.subcategory_name}</strong>
                      </td>
                      <td>
                        <small className="text-muted">
                          {categories.find(
                            (c) => c.category_id == sub.category_id
                          )?.category_name || "N/A"}
                        </small>
                      </td>
                      <td>
                        <span
                          className={`badge ${sub.is_active ? "bg-success" : "bg-danger"
                            }`}
                          title={sub.is_active ? "Active" : "Inactive"}
                        >
                          {sub.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex justify-content-end align-items-center gap-2">
                          <button
                            className="btn btn-outline-primary btn-sm"
                            title="Edit"
                            onClick={() => handleEditSubcategory(sub)}
                            disabled={
                              subCatEditLoadingId == sub.subcategory_id
                            }
                          >
                            {subCatEditLoadingId == sub.subcategory_id ? (
                              <span className="spinner-border spinner-border-sm" />
                            ) : (
                              <FaEdit />
                            )}
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm"
                            title="Delete"
                            onClick={() =>
                              confirmDeleteSubcategory(sub.subcategory_id)
                            }
                            disabled={
                              subCatDeleteLoadingId == sub.subcategory_id
                            }
                          >
                            {subCatDeleteLoadingId == sub.subcategory_id ? (
                              <span className="spinner-border spinner-border-sm" />
                            ) : (
                              <FaTrash />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
